import {
  CognitoUser,
  AuthenticationDetails,
  CognitoRefreshToken
} from "amazon-cognito-identity-js";

import { Action } from "./actions";
import { mkAttrList, sendAttributeVerificationCode } from "./attributes";
import { buildLogins } from "./utils";

/**
 * sends the email verification code and transitions to the correct state
 * @param {object} user - the CognitoUser object
 * @param {object} attributes - the attributes dictionary
 * @return {Promise<object>} a promise that resolves to a redux action
 */
const emailVerificationFlow = (user, attributes) =>
  new Promise(resolve =>
    sendAttributeVerificationCode(user, "email").then(
      required => {
        if (required) {
          resolve(Action.emailVerificationRequired(attributes));
        } else {
          // dead end?
          resolve(Action.loggingIn(attributes));
        }
      },
      error => {
        // some odd classes of error here
        resolve(Action.emailVerificationFailed(error, attributes));
      }
    )
  );

/**
 * logs in to the federated identity pool with a JWT
 * @param {string} username - the username
 * @param {string} jwtToken - a token from the session
 * @param {object} config - the react-cognito config
 * @return {Promise<object>} a promise that resolves to the federated identity credentials
 */
// const refreshIdentityCredentials = (username, jwtToken, config) =>
//   new Promise((resolve, reject) => {
//     const logins = buildLogins(username, jwtToken, config);
//     const creds = new CognitoIdentityCredentials(logins);
//     creds.refresh(error => {
//       if (error) {
//         reject(error.message);
//       } else {
//         resolve(creds);
//       }
//     });
//   });

/**
 * establishes a session with the user pool, and logs into the federated identity
 * pool using a token from the session
 * @param {object} user - the CognitoUser object
 * @param {object} config -the react-cognito config
 * @return {Promise<object>} an action to be dispatched
 */
// const performLogin = (user, config) =>
//   new Promise((resolve, reject) => {
//     if (user != null) {
//       user.getSession((err, session) => {
//         if (err) {
//           resolve(Action.loginFailure(user, err.message));
//         } else {
//           const jwtToken = session.getIdToken().getJwtToken();
//           const username = user.getUsername();
//           refreshIdentityCredentials(username, jwtToken, config).then(
//             creds => resolve(Action.login(creds)),
//             message => resolve(Action.loginFailure(user, message))
//           );
//         }
//       });
//     } else {
//       reject(new Error("user is null"));
//     }
//   });

/**
 *
 * Authenticates with a user pool, and handles responses.
 * if the authentication is successful it then logs in to the
 * identity pool.
 *
 * returns an action depending on the outcome.  Possible actions returned
 * are:
 *
 * - login - valid user who is logged in
 * - loginFailure - failed to authenticate with user pool or identity pool
 * - mfaRequired - user now needs to enter MFA
 * - newPasswordRequired - user must change password on first login
 * - emailVerificationRequired - user must verify their email address
 * - emailVerificationFailed - email verification is required, but won't work
 *
 * Dispatch the resulting action, e.g.:
 *
 * ```
 * const { userPool, config } = state.cognito;
 * authenticate(username, password, userPool, config).then(dispatch);
 * ```
 *
 * @param {string} username - the username provided by the user
 * @param {string} password - the password provided by the user
 * @param {object} userPool - a Cognito User Pool object
 * @return {Promise<object>} - a promise that resolves an action to be dispatched
 *
 */
const authenticate = (username, password, userPool, config, dispatch) =>
  new Promise((resolve, reject) => {
    const creds = new AuthenticationDetails({
      Username: username,
      Password: password
    });

    const user = new CognitoUser({
      Username: username,
      Pool: userPool
    });

    user.authenticateUser(creds, {
      onSuccess: () => {
        dispatch(Action.authenticated(user));
        resolve();
      },
      newPasswordRequired(userAttributes) {
        // User was signed up by an admin and must provide new
        // password and required attributes, if any, to complete
        // authentication.

        // the api doesn't accept this field back
        delete userAttributes.email_verified;

        // unsure about this field, but I don't send this back
        delete userAttributes.phone_number_verified;

        // Get these details and call
        user.completeNewPasswordChallenge("Kavinda-u3", userAttributes, this);
      },
      onFailure: error => {
        if (error.code === "UserNotConfirmedException") {
          dispatch(Action.confirmationRequired(user));
          resolve();
        } else {
          dispatch(Action.loginFailure(user, error.message));
          reject(error);
        }
      },
      mfaRequired: () => {
        dispatch(Action.mfaRequired(user));
        resolve();
      }
      // newPasswordRequired: () => {
      //   dispatch(Action.newPasswordRequired(user));
      //   resolve();
      // },
    });
  });

const refreshToken = (username, userPool, dispatch) =>
  new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: username,
      Pool: userPool
    });
    const token =
      cognitoUser.storage[
        "CognitoIdentityServiceProvider." +
          userPool.clientId +
          "." +
          username +
          ".refreshToken"
      ];
    const cognitoRefreshToken = new CognitoRefreshToken({
      RefreshToken: token
    });
    cognitoUser.refreshSession(cognitoRefreshToken, (error, response) => {
      if (error) {
        reject(error.message);
      } else {
        dispatch(Action.refreshed(cognitoUser));
        resolve();
      }
    });
  });

/**
 * sign up this user with the user pool provided
 * @param {object} userPool - a Cognito userpool (e.g. state.cognito.userPool)
 * @param {object} config - the react-cognito config object
 * @param {string} username - the username
 * @param {string} password - the password
 * @param {object} attributes - an attributes dictionary
 * @return {Promise<object>} a promise that resolves a redux action
 */
const registerUser = (userPool, config, username, password, attributes) =>
  new Promise((resolve, reject) =>
    userPool.signUp(
      username,
      password,
      mkAttrList(attributes),
      null,
      (err, result) => {
        if (err) {
          reject(err.message);
        } else if (result.userConfirmed === false) {
          resolve(Action.confirmationRequired(result.user, attributes.email));
        } else {
          resolve(
            Action.userRegistered(result.user, attributes.email, result.userSub)
          );

          // createUser(otherAttr)
          // .then(u => {
          //   resolve(Action.userRegistered(result.user, attributes.email));
          // });
        }
      }
    )
  );

export {
  authenticate,
  //performLogin,
  registerUser,
  emailVerificationFlow,
  refreshToken
};
